import React, { useState, useEffect, useMemo, useRef } from 'react';
import './SplitViewMatcheo.css';

const CLOUD_FUNCTIONS_BASE = 'https://us-central1-abbott-matcher-60171.cloudfunctions.net';

// Base de conocimiento farmacéutico
const PHARMA_KNOWLEDGE = {
  // Sinónimos de formas farmacéuticas
  formasFarmaceuticas: {
    'capsula': ['caps', 'capsulas', 'cápsula', 'cápsulas'],
    'tableta': ['tab', 'tabletas', 'comprimido', 'comprimidos'],
    'jarabe': ['suspension', 'suspensión', 'solución oral', 'solucion oral'],
    'crema': ['ungüento', 'unguento', 'pomada', 'gel'],
    'inyectable': ['ampolla', 'ampollas', 'vial', 'inyeccion', 'inyección'],
    'polvo': ['sobres', 'sobre', 'granulado'],
  },

  // Formas a excluir según el tipo
  formasExcluir: {
    'oral': ['crema', 'ungüento', 'unguento', 'pomada', 'gel', 'inyectable', 'supositorio'],
    'topica': ['jarabe', 'suspension', 'tableta', 'capsula', 'inyectable'],
    'inyectable': ['jarabe', 'suspension', 'tableta', 'capsula', 'crema', 'pomada'],
  },

  // Equivalentes de principios activos comunes
  principiosActivos: {
    'paracetamol': ['acetaminofen', 'acetaminofén'],
    'ibuprofeno': ['ibuprofen'],
    'fluconazol': ['antifungico', 'antifúngico'],
    'baten': ['fluconazol', 'antifungico', 'antifúngico'],
    'amoxicilina': ['amoxicillin'],
    'losartan': ['antihipertensivo'],
    'metformina': ['metformin'],
    'omeprazol': ['omeprazole'],
    'atorvastatina': ['atorvastatin'],
  },

  // Laboratorios equivalentes comunes por droga
  laboratorios: {
    'fluconazol': ['genfar', 'mk', 'la sante', 'icom', 'american generics', 'premium pharma', 'fuzol'],
    'paracetamol': ['genfar', 'mk', 'tecnoquimicas', 'lafrancol', 'colmed'],
    'ibuprofeno': ['genfar', 'mk', 'tecnoquimicas', 'lafrancol'],
    'amoxicilina': ['genfar', 'mk', 'la sante', 'tecnoquimicas'],
    'default': ['genfar', 'mk', 'la sante', 'tecnoquimicas', 'lafrancol', 'colmed'],
  },
};

// Componente reutilizable para cards de productos de catálogo
const CatalogProductCard = ({
  product,
  formatPrice,
  copyToClipboard,
  openImageModal,
  headerBadges = null,
  footerBadge = null,
  searchTerms = [],
  highlightText = (text) => text,
  className = '',
  style = {},
  onClick = null,
  draggable = false,
  onDragStart = null,
  onDragEnd = null,
  cardId = null
}) => {
  const [isDragging, setIsDragging] = React.useState(false);

  const hasOriginal = product.original_price && !isNaN(parseFloat(product.original_price)) && parseFloat(product.original_price) > 0;
  const hasDiscounted = product.discounted_price && !isNaN(parseFloat(product.discounted_price)) && parseFloat(product.discounted_price) > 0;
  const hasSale = product.sale_price && !isNaN(parseFloat(product.sale_price)) && parseFloat(product.sale_price) > 0;
  const hasAnyPrice = hasOriginal || hasDiscounted || hasSale;

  const hasDiscount = hasOriginal && hasSale && parseFloat(product.original_price) > parseFloat(product.sale_price);
  const discountPercent = hasDiscount
    ? Math.round(((parseFloat(product.original_price) - parseFloat(product.sale_price)) / parseFloat(product.original_price)) * 100)
    : 0;

  const handleClick = (e) => {
    if (!isDragging && onClick) {
      onClick(e);
    }
    setIsDragging(false);
  };

  const handleDragStartInternal = (e) => {
    setIsDragging(true);
    if (onDragStart) {
      onDragStart(e);
    }
  };

  return (
    <div
      id={cardId}
      className={`product-card ${className}`}
      style={style}
      onClick={handleClick}
      draggable={draggable}
      onDragStart={handleDragStartInternal}
      onDragEnd={onDragEnd}
    >
      <div className="product-content-with-image">
        {product.imagen && (
          <div
            className="product-image-container-corner"
            onClick={(e) => {
              e.stopPropagation();
              openImageModal(product.imagen, product.title);
            }}
          >
            <img
              src={product.imagen}
              alt={product.title}
              className="product-image"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="image-zoom-overlay">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
          </div>
        )}
        <div className="product-info">
          <div className="product-card-header">
            <div className="product-name">{highlightText(product.title, searchTerms)}</div>
            {headerBadges && <div className="product-card-actions">{headerBadges}</div>}
          </div>

          {/* Precios */}
          <div className="product-prices">
            {hasAnyPrice ? (
              <>
                {hasOriginal && (
                  <div className="price-row">
                    <span className="price-label">Original:</span>
                    <span className={`price-original ${hasDiscount ? 'strikethrough' : ''}`}>
                      {formatPrice(product.original_price)}
                    </span>
                  </div>
                )}

                {hasDiscounted && (
                  <div className="price-row">
                    <span className="price-label">Descuento:</span>
                    <span className="price-sale">
                      {formatPrice(product.discounted_price)}
                      {hasDiscount && (
                        <span className="price-discount-inline"> (-{discountPercent}%)</span>
                      )}
                    </span>
                  </div>
                )}

                {hasSale && (
                  <div className="price-row">
                    <span className="price-label">Precio Venta:</span>
                    <span className="price-sale">
                      {formatPrice(product.sale_price)}
                      {hasDiscount && (
                        <span className="price-discount-inline"> (-{discountPercent}%)</span>
                      )}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="price-row">
                <span className="price-label">Precio:</span>
                <span className="price-sale">N/A</span>
              </div>
            )}
          </div>

          <div className="product-details">
            <div className="product-meta-row-compact">
              <span className="meta-text-small">MARCA: <span>{highlightText(product.marca || 'N/A', searchTerms)}</span></span>
              <span className="separator">|</span>
              <span className="meta-text-small">SITIO: <span>{highlightText(product.sitio || 'N/A', searchTerms)}</span></span>
              {product.url && (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="product-link-btn"
                  onClick={(e) => e.stopPropagation()}
                  title="Ver publicación original"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </a>
              )}
            </div>
            {product.principioActivo && <div><strong>Droga:</strong> <span>{highlightText(product.principioActivo, searchTerms)}</span></div>}
            {product.presentacion && <div><strong>Presentación:</strong> <span>{highlightText(product.presentacion, searchTerms)}</span></div>}
          </div>
        </div>
      </div>
      <div className="product-code-footer">
        <div className="footer-left">
          <span>{product.id}</span>
          <button
            className="copy-id-btn"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(product.id);
            }}
            title="Copiar ID"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
        {footerBadge}
      </div>
    </div>
  );
};

const SplitViewMatcheo = () => {
  // Función para formatear precios
  const formatPrice = (price) => {
    if (!price || isNaN(parseFloat(price))) return 'N/A';

    const num = parseFloat(price);
    const hasDecimals = num % 1 !== 0;

    // Si tiene decimales, mostrarlos con 2 dígitos
    // Si no tiene decimales, mostrar solo la parte entera
    const formatted = hasDecimals ? num.toFixed(2) : num.toFixed(0);

    // Separar parte entera y decimal
    const [integerPart, decimalPart] = formatted.split('.');

    // Agregar separador de miles (punto)
    const integerWithSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    // Si hay decimales, usar coma como separador decimal
    if (decimalPart && parseInt(decimalPart) > 0) {
      return `$${integerWithSeparator},${decimalPart}`;
    }

    return `$${integerWithSeparator}`;
  };

  // Estado de búsqueda principal
  const [mainSearchText, setMainSearchText] = useState('');
  const [mainActiveSearch, setMainActiveSearch] = useState('');

  // Estados para panel izquierdo (Maestro)
  const [masterProducts, setMasterProducts] = useState([]);
  const [masterFilterText, setMasterFilterText] = useState('');
  const [masterActiveFilter, setMasterActiveFilter] = useState('');
  const [masterMatchFilter, setMasterMatchFilter] = useState('all'); // 'all', 'with_match', 'without_match'
  const [masterPagination, setMasterPagination] = useState({ offset: 0, limit: 50 });
  const [masterTotal, setMasterTotal] = useState(0);
  const [masterLoading, setMasterLoading] = useState(false);
  const [selectedMasterProduct, setSelectedMasterProduct] = useState(null);

  // Estados para panel derecho (Toma/Intake)
  const [intakeProducts, setIntakeProducts] = useState([]);
  const [intakeFilterText, setIntakeFilterText] = useState('');
  const [intakeActiveFilter, setIntakeActiveFilter] = useState('');
  const [intakeMatchFilter, setIntakeMatchFilter] = useState('all'); // 'all', 'with_match', 'without_match'
  const [intakePagination, setIntakePagination] = useState({ offset: 0, limit: 50 });
  const [intakeTotal, setIntakeTotal] = useState(0);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [selectedIntakeProducts, setSelectedIntakeProducts] = useState(new Set());
  const [selectedIntakeProduct, setSelectedIntakeProduct] = useState(null); // Producto individual seleccionado
  const [isViewingMasterMatches, setIsViewingMasterMatches] = useState(false); // Flag para saber si estamos viendo matches de un maestro

  // Estados para vista intermedia de matches
  const [showMatchesPanel, setShowMatchesPanel] = useState(false);
  const [matchesPanelData, setMatchesPanelData] = useState(null); // { masterProduct, matches, priceStats }
  const [matchesPanelLoading, setMatchesPanelLoading] = useState(false);
  const [isAnimatingPanelOpen, setIsAnimatingPanelOpen] = useState(false);
  const [isCreatingMatches, setIsCreatingMatches] = useState(false);

  // Estados compartidos
  const [matchResults, setMatchResults] = useState({}); // { codigo: [matches] }
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchModalData, setMatchModalData] = useState(null);
  const [isSavingMatch, setIsSavingMatch] = useState(false);

  // Estados para AI Matching
  const [aiSuggestedMatches, setAiSuggestedMatches] = useState(new Set()); // Set de IDs de productos sugeridos por AI
  const [isAnalyzingAI, setIsAnalyzingAI] = useState(false);
  const [aiAnalysisProgress, setAiAnalysisProgress] = useState({ current: 0, total: 0 });

  // Estado para modal de imagen
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // Estado para información de la toma actual
  const [tomaInfo, setTomaInfo] = useState({ descripcion: 'TOMAS' });
  const [imageModalSrc, setImageModalSrc] = useState('');
  const [imageModalTitle, setImageModalTitle] = useState('');

  // Estado para toast de copiado
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [copiedId, setCopiedId] = useState('');

  // Estados para drag and drop
  const [draggedProduct, setDraggedProduct] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Estados para toast de matches
  const [showMatchToast, setShowMatchToast] = useState(false);
  const [matchToastMessage, setMatchToastMessage] = useState('');

  // Estados para AI Image Scanner
  const [aiImageAnalysis, setAiImageAnalysis] = useState(null);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [scanningProgress, setScanningProgress] = useState(0);

  // Refs para sincronizar scroll
  const panelsContainerRef = useRef(null);
  const headerRef = useRef(null);

  // Sincronizar scroll horizontal del header con los paneles
  useEffect(() => {
    const panelsContainer = panelsContainerRef.current;
    const header = headerRef.current;

    if (!panelsContainer || !header) return;

    const handleScroll = () => {
      // Sincronizar el scroll del header con el de los paneles
      header.scrollLeft = panelsContainer.scrollLeft;
    };

    panelsContainer.addEventListener('scroll', handleScroll);

    return () => {
      panelsContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cargar productos del maestro (siempre, incluso sin búsqueda activa para mostrar productos al inicio)
  useEffect(() => {
    loadMasterProducts();
  }, [masterPagination.offset, masterPagination.limit, masterActiveFilter, mainActiveSearch, masterMatchFilter]);

  // Cargar productos de toma (catálogo) (siempre, incluso sin búsqueda activa para mostrar productos al inicio)
  useEffect(() => {
    loadIntakeProducts();
  }, [intakePagination.offset, intakePagination.limit, intakeActiveFilter, mainActiveSearch, intakeMatchFilter]);

  // Filtrar productos de intake (excluir los que ya están en el panel de matches)
  const sortedIntakeProducts = useMemo(() => {
    // Filtrar productos que ya están en el panel de matches
    // Convertir a número para asegurar comparación correcta
    const matchedProductIds = new Set(
      (matchesPanelData?.matches || []).map(m => {
        const id = m.id;
        return typeof id === 'string' ? parseInt(id, 10) : id;
      })
    );

    const filteredProducts = intakeProducts.filter(product => {
      // Si hay panel de matches abierto, excluir productos que ya están ahí
      if (showMatchesPanel && matchedProductIds.has(product.id)) {
        return false;
      }
      return true;
    });

    // Devolver productos en su orden original (sin reordenar)
    return filteredProducts;
  }, [intakeProducts, matchesPanelData, showMatchesPanel]);

  // Cargar matches guardados y toma info al inicio
  useEffect(() => {
    loadSavedMatches();
    loadTomaInfo();
  }, []);

  const loadTomaInfo = async () => {
    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/getcurrenttoma`);
      const result = await response.json();
      if (result.success && result.data) {
        setTomaInfo(result.data);
      }
    } catch (error) {
      console.error('Error cargando info de toma:', error);
      // Mantener valor por defecto si hay error
    }
  };

  const loadMasterProducts = async () => {
    setMasterLoading(true);
    try {
      // Combinar búsqueda principal y filtro específico
      const searches = [mainActiveSearch, masterActiveFilter].filter(Boolean);
      const combinedSearch = searches.join(',');
      const searchParam = combinedSearch ? `&search=${encodeURIComponent(combinedSearch)}` : '';
      const matchFilterParam = masterMatchFilter !== 'all' ? `&matchFilter=${masterMatchFilter}` : '';
      const endpoint = `${CLOUD_FUNCTIONS_BASE}/getmaestroproducts?limit=${masterPagination.limit}&offset=${masterPagination.offset}${searchParam}${matchFilterParam}`;

      const response = await fetch(endpoint);
      const result = await response.json();

      if (result.success) {
        console.log('🔍 Raw data from backend:', result.data[0]); // Debug: ver primer producto

        const adaptedProducts = result.data.map(p => {
          // Parsear catalog_data si existe
          let catalogData = {};
          if (p.catalog_data) {
            try {
              catalogData = typeof p.catalog_data === 'string' ? JSON.parse(p.catalog_data) : p.catalog_data;
            } catch (e) {
              console.error('Error parsing catalog_data:', e);
            }
          }

          const adapted = {
            codigo: p.codigo_presentacion,
            nombre: catalogData.nombre || p.presentacion_original || p.nombre || p.presentacion,
            presentacion: p.presentacion_original || p.presentacion,
            marca: p.laboratorio_original || p.marca || p.laboratorio,
            laboratorio: p.laboratorio_original || p.laboratorio,
            principioActivo: catalogData.principio_activo || catalogData.principioActivo || p.principio_activo,
            concentracion: catalogData.concentracion || p.concentracion,
            formaFarmaceutica: catalogData.forma_farmaceutica || catalogData.formaFarmaceutica || p.forma_farmaceutica,
            corporation: catalogData.corporation || p.corporation,
            product: catalogData.product_original || catalogData.product || p.product,
            match_count: p.match_count || 0
          };

          console.log('📦 Adapted product:', adapted); // Debug: ver producto adaptado
          return adapted;
        });
        setMasterProducts(adaptedProducts);
        setMasterTotal(result.total);
      }
    } catch (error) {
      console.error('Error cargando productos maestro:', error);
    } finally {
      setMasterLoading(false);
    }
  };

  const loadIntakeProducts = async () => {
    setIntakeLoading(true);
    try {
      // Combinar búsqueda principal y filtro específico
      const searches = [mainActiveSearch, intakeActiveFilter].filter(Boolean);
      const combinedSearch = searches.join(',');
      const searchParam = combinedSearch ? `&search=${encodeURIComponent(combinedSearch)}` : '';
      const matchFilterParam = intakeMatchFilter !== 'all' ? `&matchFilter=${intakeMatchFilter}` : '';
      const endpoint = `${CLOUD_FUNCTIONS_BASE}/getcatalogproducts?limit=${intakePagination.limit}&offset=${intakePagination.offset}${searchParam}${matchFilterParam}`;

      const response = await fetch(endpoint);
      const result = await response.json();

      if (result.success) {
        const adaptedProducts = result.data.map(p => ({
          id: p.id_catalogo,
          title: p.title,
          marca: p.toma_marca || p.marca, // Usar marca de toma primero, luego fallback a catalogado
          sitio: p.sitio,
          url: p.url, // URL del producto original
          precio: p.sale_price || p.discounted_price || p.original_price, // Usar precios de toma
          original_price: p.original_price, // Precio original
          discounted_price: p.discounted_price, // Precio con descuento
          sale_price: p.sale_price, // Precio de venta
          imagen: p.imagen, // Imagen del producto
          categoria: p.categoria,
          formaFarmaceutica: p.forma_farmaceutica,
          principioActivo: p.principio_activo,
          presentacion: p.presentacion,
          concentracion: p.concentracion,
          laboratorio: p.laboratorio,
          stock: p.stock,
          sku: p.sku,
          match_count: p.match_count || 0  // Conteo de productos maestros matcheados
        }));
        setIntakeProducts(adaptedProducts);
        setIntakeTotal(result.total);
      }
    } catch (error) {
      console.error('Error cargando productos de toma:', error);
    } finally {
      setIntakeLoading(false);
    }
  };

  const loadMatchedCatalogProducts = async (masterProduct) => {
    // Abrir panel inmediatamente - el flexbox empujará naturalmente el panel de catálogo
    setMatchesPanelData({
      masterProduct,
      matches: [],
      priceStats: null
    });
    setShowMatchesPanel(true);
    setMatchesPanelLoading(true);

    // Seleccionar el producto maestro para indicar visualmente la relación
    setSelectedMasterProduct(masterProduct);

    try {
      const masterCode = masterProduct.codigo_presentacion || masterProduct.codigo;
      const endpoint = `${CLOUD_FUNCTIONS_BASE}/getmatchedcatalogproducts?id_maestro=${masterCode}`;
      console.log('Loading matches for master product:', masterCode, endpoint);
      const response = await fetch(endpoint);
      const result = await response.json();

      if (result.success && result.data.length > 0) {
        const adaptedProducts = result.data.map(p => ({
          id: p.id_catalogo,
          title: p.title,
          marca: p.toma_marca || p.marca,
          sitio: p.sitio,
          url: p.url,
          precio: p.sale_price || p.discounted_price || p.original_price,
          original_price: p.original_price,
          discounted_price: p.discounted_price,
          sale_price: p.sale_price,
          imagen: p.imagen,
          categoria: p.categoria,
          formaFarmaceutica: p.forma_farmaceutica,
          principioActivo: p.principio_activo,
          presentacion: p.presentacion,
          concentracion: p.concentracion,
          laboratorio: p.laboratorio,
          stock: p.stock,
          sku: p.sku
        }));

        // Ordenar productos por precio de venta (menor a mayor)
        const sortedProducts = adaptedProducts.sort((a, b) => {
          const priceA = parseFloat(a.sale_price) || Infinity;
          const priceB = parseFloat(b.sale_price) || Infinity;
          return priceA - priceB;
        });

        // Calcular estadísticas de precios
        const salePrices = sortedProducts
          .map(p => parseFloat(p.sale_price))
          .filter(p => !isNaN(p) && p > 0);

        // Solo mostrar estadísticas si hay más de 1 producto y los precios son diferentes
        let priceStats = null;
        if (salePrices.length > 1) {
          const uniquePrices = [...new Set(salePrices)];
          if (uniquePrices.length > 1) {
            priceStats = {
              average: salePrices.reduce((a, b) => a + b, 0) / salePrices.length,
              min: Math.min(...salePrices),
              max: Math.max(...salePrices),
              count: salePrices.length
            };
          }
        }

        // Pequeño delay para permitir que la animación de apertura se complete
        await new Promise(resolve => setTimeout(resolve, 150));

        // Actualizar con datos reales
        setMatchesPanelData({
          masterProduct,
          matches: sortedProducts,
          priceStats
        });
        setMatchesPanelLoading(false);

        console.log(`Cargados ${result.total} productos matcheados para ${masterProduct.nombre || masterProduct.presentacion}`);
      } else {
        // Mostrar panel vacío con mensaje de invitación
        setMatchesPanelData({
          masterProduct,
          matches: [],
          priceStats: null
        });
        setMatchesPanelLoading(false);
      }
    } catch (error) {
      setMatchesPanelLoading(false);
      setShowMatchesPanel(false);
      console.error('Error cargando productos matcheados:', error);
      alert('Error cargando matches: ' + error.message);
    }
  };

  // Cargar productos del maestro matcheados con un producto del catálogo
  const loadMatchedMasterProducts = async (catalogProduct) => {
    setMasterLoading(true);
    try {
      // Limpiar búsquedas solo del maestro
      setMasterActiveFilter('');
      setMasterFilterText('');

      // Seleccionar el producto de catálogo para mostrar la asociación
      setSelectedIntakeProduct(catalogProduct);
      setSelectedMasterProduct(null); // Limpiar selección de maestro
      setAiSuggestedMatches(new Set()); // Limpiar sugerencias de AI

      const endpoint = `${CLOUD_FUNCTIONS_BASE}/getmatchedmasterproducts?id_catalogo=${catalogProduct.id}`;
      const response = await fetch(endpoint);
      const result = await response.json();

      if (result.success && result.data.length > 0) {
        const adaptedProducts = result.data.map(p => ({
          codigo: p.codigo_presentacion,
          nombre: p.nombre,
          presentacion: p.presentacion,
          marca: p.marca,
          laboratorio: p.laboratorio,
          principioActivo: p.principio_activo,
          concentracion: p.concentracion,
          formaFarmaceutica: p.forma_farmaceutica,
          corporation: p.corporation,
          product: p.product,
          match_count: 0  // Los productos del maestro no tienen conteo inverso en esta vista
        }));

        setMasterProducts(adaptedProducts);
        setMasterTotal(result.total);

        console.log(`Cargados ${result.total} productos maestros matcheados con ${catalogProduct.title || catalogProduct.id}`);
      } else {
        console.log('No hay productos maestros matcheados con este producto del catálogo');
        setMasterProducts([]);
        setMasterTotal(0);
      }
    } catch (error) {
      console.error('Error cargando productos maestros matcheados:', error);
      setMasterProducts([]);
      setMasterTotal(0);
    } finally {
      setMasterLoading(false);
    }
  };

  const loadSavedMatches = async () => {
    console.log('🔄 Iniciando loadSavedMatches...');
    try {
      const pageSize = 5000;
      let offset = 0;
      let totalMatches = Number.POSITIVE_INFINITY;
      const allMatches = [];

      while (offset === 0 || offset < totalMatches) {
        const endpoint = `${CLOUD_FUNCTIONS_BASE}/getsavedmatches?limit=${pageSize}&offset=${offset}`;
        console.log('📡 Llamando a:', endpoint);

        const response = await fetch(endpoint);
        console.log('📡 Respuesta recibida, status:', response.status);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const received = result.matches?.length || 0;
        console.log('📦 Página de resultados:', {
          offset,
          recibidos: received,
          total: result.total
        });

        if (!result.matches || result.matches.length === 0) {
          break;
        }

        allMatches.push(...result.matches);

        if (typeof result.total === 'number') {
          totalMatches = result.total;
        }

        if (received < pageSize) {
          break;
        }

        offset += pageSize;
      }

      if (allMatches.length === 0) {
        console.warn('⚠️ No se recibieron matches desde el backend');
        setMatchResults({});
        return;
      }

      console.log('📦 Total matches acumulados:', allMatches.length);

      const firstMatch = allMatches[0];
      if (firstMatch) {
        console.log('🔍 Primer match raw del servidor:', firstMatch);
      }

      const spotlightMatch = allMatches.find(match =>
        match?.codigo_presentacion === '1391101' || match?.match_id === '7693'
      );
      if (spotlightMatch) {
        console.log('🎯 ENCONTRADO match maestro 1391101 o catálogo 7693:', spotlightMatch);
      }

      const groupedMatches = {};
      const dedupeSet = new Set();

      allMatches.forEach(match => {
        if (!match || !match.codigo_presentacion || !match.match_id) {
          return;
        }

        const dedupeKey = `${match.codigo_presentacion}-${match.match_id}`;
        if (dedupeSet.has(dedupeKey)) {
          return;
        }
        dedupeSet.add(dedupeKey);

        const masterKey = match.codigo_presentacion;
        if (!groupedMatches[masterKey]) {
          groupedMatches[masterKey] = [];
        }

        const parsedCatalogId = Number.parseInt(match.match_id, 10);
        const parsedConfidence = Number.parseInt(match.confidence, 10);

        groupedMatches[masterKey].push({
          id: match.match_id,
          catalogId: Number.isNaN(parsedCatalogId) ? match.match_id : parsedCatalogId,
          title: match.match_title,
          marca: match.match_marca,
          sitio: match.match_sitio,
          confidence: Number.isNaN(parsedConfidence) ? match.confidence : parsedConfidence
        });
      });

      console.log('📦 Matches agrupados por maestro:', {
        totalMasters: Object.keys(groupedMatches).length,
        totalMatchesAgrupados: Object.values(groupedMatches).flat().length,
        tiene1391101: groupedMatches['1391101'] ? 'SÍ' : 'NO',
        matchesDe1391101: groupedMatches['1391101']
      });

      setMatchResults(groupedMatches);
      console.log('✅ matchResults actualizado exitosamente');
    } catch (error) {
      console.error('❌ Error cargando matches guardados:', error);
      setMatchResults({});
    }
  };

  // Función para buscar matches de un producto de intake
  const getIntakeProductMatches = (catalogId) => {
    // Buscar en qué productos maestros está este catalogId
    const matchedMasters = [];

    // Debug: Log para ver qué estamos buscando
    if (catalogId === 7693 || catalogId === '7693') { // Cambia este ID por uno que sepas que tiene matches
      console.log('🔍 Buscando matches para catalogId:', catalogId, {
        tipo: typeof catalogId,
        matchResultsKeys: Object.keys(matchResults),
        totalMaestros: Object.keys(matchResults).length
      });
    }

    Object.entries(matchResults).forEach(([masterCodigo, matches]) => {
      // Debug: Log para el producto 7693 - ver todos los catalogIds de este maestro
      if (catalogId === 7693 || catalogId === '7693') {
        console.log(`🔎 Maestro ${masterCodigo}:`, {
          totalMatches: matches.length,
          catalogIds: matches.map(m => ({ id: m.catalogId, tipo: typeof m.catalogId })).slice(0, 3)
        });
      }

      // Comparar tanto como número como string por si hay mismatch de tipos
      const match = matches.find(m => m.catalogId == catalogId || String(m.catalogId) === String(catalogId));

      // Debug: Log cuando encontramos algo
      if (match && (catalogId === 7693 || catalogId === '7693')) {
        console.log('✅ Match encontrado!', {
          masterCodigo,
          matchCatalogId: match.catalogId,
          tipoCatalogId: typeof match.catalogId
        });
      }

      if (match) {
        matchedMasters.push(masterCodigo);
      }
    });

    // Debug: Log del resultado
    if (catalogId === 7693 || catalogId === '7693') {
      console.log('📊 Resultado final:', matchedMasters);
    }

    return matchedMasters;
  };

  // Handler para búsqueda en panel maestro
  const handleMasterSearch = () => {
    setMasterActiveFilter(masterFilterText);
    setMasterPagination(prev => ({ ...prev, offset: 0 }));
    // Limpiar productos de referencia al hacer nueva búsqueda
    setSelectedIntakeProduct(null);
  };

  const handleMasterClearFilter = () => {
    setMasterFilterText('');
    setMasterActiveFilter('');
    setMasterPagination(prev => ({ ...prev, offset: 0 }));
    // Limpiar productos de referencia al limpiar búsqueda
    setSelectedIntakeProduct(null);
  };

  // Handler para búsqueda en panel intake
  const handleIntakeSearch = () => {
    setIntakeActiveFilter(intakeFilterText);
    setIntakePagination(prev => ({ ...prev, offset: 0 }));
    // Limpiar selecciones y productos de referencia al hacer nueva búsqueda
    setSelectedIntakeProducts(new Set());
    setSelectedMasterProduct(null);
    setIsViewingMasterMatches(false);
  };

  const handleIntakeClearFilter = () => {
    setIntakeFilterText('');
    setIntakeActiveFilter('');
    setIntakePagination(prev => ({ ...prev, offset: 0 }));
    // Limpiar selecciones y productos de referencia al limpiar búsqueda
    setSelectedIntakeProducts(new Set());
    setSelectedMasterProduct(null);
    setIsViewingMasterMatches(false);
  };

  // Selección de productos
  const toggleIntakeSelection = (productId) => {
    setSelectedIntakeProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const selectMasterProduct = async (product) => {
    // Si el producto ya está seleccionado, cerrar el panel y deseleccionar
    if (selectedMasterProduct?.codigo === product.codigo) {
      closeMatchesPanel();
      return;
    }

    // Siempre abrir el panel (aunque no tenga matches)
    loadMatchedCatalogProducts(product);
  };

  // Resetear panel maestro
  const handleResetMasterPanel = async () => {
    setSelectedMasterProduct(null);
    setSelectedIntakeProduct(null);
    setSelectedIntakeProducts(new Set());
    setIsViewingMasterMatches(false);

    // Limpiar todos los filtros y resultados
    setMasterProducts([]);
    setMasterTotal(0);
    setMasterFilterText('');
    setMasterActiveFilter('');
    setMasterPagination({ offset: 0, limit: 50 });
    setMasterMatchFilter('all');
  };

  // Resetear panel catálogo
  const handleResetIntakePanel = () => {
    setIntakeProducts([]);
    setIntakeTotal(0);
    setIntakeFilterText('');
    setIntakeActiveFilter('');
    setIntakePagination({ offset: 0, limit: 50 });
    setSelectedIntakeProduct(null);
    setSelectedIntakeProducts(new Set());
    setSelectedMasterProduct(null);
    setAiSuggestedMatches(new Set());
    setIsViewingMasterMatches(false);
  };

  // Función para escapar caracteres especiales de regex
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Función para highlight de texto
  const highlightText = (text, searchTerms) => {
    if (!text || !searchTerms || searchTerms.length === 0) return text;

    let highlightedText = String(text);
    searchTerms.forEach(term => {
      if (term && term.length > 0) {
        // Limpiar prefijos AND:, OR:, NOT: antes de resaltar
        let cleanTerm = term;
        if (term.toUpperCase().startsWith('AND:(') || term.toUpperCase().startsWith('OR:(') || term.toUpperCase().startsWith('NOT:(')) {
          return; // No resaltar términos anidados con paréntesis
        }
        if (term.toUpperCase().startsWith('AND:')) {
          cleanTerm = term.substring(4);
        } else if (term.toUpperCase().startsWith('OR:')) {
          cleanTerm = term.substring(3);
        } else if (term.toUpperCase().startsWith('NOT:')) {
          cleanTerm = term.substring(4);
        }

        // Escapar caracteres especiales de regex
        const escapedTerm = escapeRegex(cleanTerm.trim());
        if (escapedTerm.length > 0) {
          const regex = new RegExp(`(${escapedTerm})`, 'gi');
          highlightedText = highlightedText.replace(regex, '<mark style="background-color: #fef3c7; padding: 2px; border-radius: 2px;">$1</mark>');
        }
      }
    });

    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // Obtener todos los términos de búsqueda activos
  const getAllSearchTerms = (additionalTerm = '') => {
    const terms = [mainActiveSearch, additionalTerm].filter(Boolean);
    return terms.flatMap(t => t.split(',').map(s => s.trim()).filter(s => s.length > 0));
  };

  // Handler para búsqueda principal
  const handleMainSearch = () => {
    setMainActiveSearch(mainSearchText);
    // Reset pagination de ambos paneles
    setMasterPagination(prev => ({ ...prev, offset: 0 }));
    setIntakePagination(prev => ({ ...prev, offset: 0 }));
    // Limpiar selecciones y productos de referencia al hacer nueva búsqueda principal
    setSelectedIntakeProducts(new Set());
    setSelectedMasterProduct(null);
    setSelectedIntakeProduct(null);
    setIsViewingMasterMatches(false);
  };

  const handleMainClearSearch = () => {
    setMainSearchText('');
    setMainActiveSearch('');
    setMasterPagination(prev => ({ ...prev, offset: 0 }));
    setIntakePagination(prev => ({ ...prev, offset: 0 }));
    // Limpiar selecciones y productos de referencia al limpiar búsqueda principal
    setSelectedIntakeProducts(new Set());
    setSelectedMasterProduct(null);
    setSelectedIntakeProduct(null);
    setIsViewingMasterMatches(false);
  };

  // Mostrar modal con matches
  const showMatchesModal = (codigo, matches, type) => {
    setMatchModalData({ codigo, matches, type });
    setShowMatchModal(true);
  };

  const handleCatalogMatchBadgeClick = async (event, product, matchedMasters) => {
    event.stopPropagation();

    // Cerrar panel de matches si está abierto
    if (showMatchesPanel) {
      closeMatchesPanel();
    }

    let matchedCodigos = [];

    // Si ya tenemos los maestros relacionados en memoria, los usamos directamente
    if (matchedMasters.length > 0) {
      matchedCodigos = matchedMasters;
    } else {
      // Si no, los obtenemos del backend
      try {
        const endpoint = `${CLOUD_FUNCTIONS_BASE}/getmatchedmasterproducts?id_catalogo=${product.id}`;
        const response = await fetch(endpoint);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data.length > 0) {
          matchedCodigos = result.data.map(item => item.codigo_presentacion);
        } else {
          alert('No se encontraron productos maestro asociados a este producto de catálogo.');
          return;
        }
      } catch (error) {
        console.error('Error cargando maestros asociados al producto de catálogo:', error);
        alert('Error cargando la información de matches de este producto.');
        return;
      }
    }

    // Filtrar la tabla de maestro para mostrar solo los productos matcheados
    if (matchedCodigos.length > 0) {
      // Guardar el producto de catálogo seleccionado y activar flag
      setSelectedIntakeProduct(product);
      setIsViewingMasterMatches(true);

      // Cargar los productos maestro filtrados
      setMasterLoading(true);
      setMasterProducts([]);

      try {
        // Hacer una consulta que filtre por los códigos matcheados
        const codigosParam = matchedCodigos.join(',');
        const endpoint = `${CLOUD_FUNCTIONS_BASE}/getmaestroproducts?codigos=${encodeURIComponent(codigosParam)}`;
        const response = await fetch(endpoint);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setMasterProducts(result.data);
          setMasterTotal(result.data.length);
          setMasterPagination({ offset: 0, limit: 50 });
        }
      } catch (error) {
        console.error('Error cargando productos maestro filtrados:', error);
        alert('Error cargando los productos maestro.');
      } finally {
        setMasterLoading(false);
      }
    }
  };

  // Función auxiliar para parsear presentación y extraer datos
  const parsePresentacion = (texto) => {
    if (!texto) return {};

    const result = {
      marca: '',
      formaFarmaceutica: '',
      concentracion: '',
      palabrasClave: []
    };

    // Extraer marca (primera palabra antes de espacio)
    const palabras = texto.trim().split(/\s+/);
    if (palabras.length > 0) {
      result.marca = palabras[0];
    }

    // Detectar forma farmacéutica
    const formas = {
      'caps': 'capsula',
      'capsula': 'capsula',
      'capsulas': 'capsula',
      'tab': 'tableta',
      'tableta': 'tableta',
      'tabletas': 'tableta',
      'comp': 'tableta',
      'comprimido': 'tableta',
      'jarabe': 'jarabe',
      'suspension': 'jarabe',
      'susp': 'jarabe',
      'crema': 'crema',
      'pomada': 'crema',
      'iny': 'inyectable',
      'inyectable': 'inyectable',
      'ampolla': 'inyectable',
    };

    const textoLower = texto.toLowerCase();
    Object.keys(formas).forEach(key => {
      if (textoLower.includes(key)) {
        result.formaFarmaceutica = formas[key];
      }
    });

    // Extraer concentración (número + unidad)
    const matchConc = texto.match(/(\d+)\s*(mg|g|ml|mcg|ui)/i);
    if (matchConc) {
      result.concentracion = matchConc[0];
    }

    // Extraer cantidad/presentación (x1, x2, x 1, x 2, etc.)
    const matchCantidad = texto.match(/x\s*(\d+)/i);
    if (matchCantidad) {
      result.cantidad = matchCantidad[1]; // Solo el número
    }

    // Extraer todas las palabras para búsqueda
    result.palabrasClave = palabras.filter(p => p.length > 2);

    return result;
  };

  // Función para generar query inteligente desde producto maestro
  const generateSmartQuery = (masterProduct) => {
    if (!masterProduct) {
      console.log('❌ No hay producto maestro');
      return '';
    }

    console.log('📋 Producto maestro recibido:', masterProduct);

    const queryParts = [];

    // Extraer información del producto (con fallback a parsear presentación)
    const presentacion = masterProduct.presentacion || masterProduct.nombre || '';
    const parsed = parsePresentacion(presentacion);

    const marca = masterProduct.marca || parsed.marca || '';
    const principioActivo = masterProduct.principioActivo || '';
    const concentracion = masterProduct.concentracion || parsed.concentracion || '';
    const formaFarmaceutica = masterProduct.formaFarmaceutica || parsed.formaFarmaceutica || '';
    const cantidad = parsed.cantidad || '';

    console.log('📊 Datos extraídos:', { marca, principioActivo, concentracion, formaFarmaceutica, cantidad, parsed });

    // 1. GRUPO AND con ORs internos: (marca OR principioActivo OR sinónimos)
    const principalTerms = [];

    // Siempre agregar la marca si existe
    if (marca && marca.toLowerCase() !== 'n/a') {
      const marcaLower = marca.toLowerCase();
      principalTerms.push(marcaLower);

      // Buscar principio activo asociado a la marca en PHARMA_KNOWLEDGE
      Object.keys(PHARMA_KNOWLEDGE.principiosActivos).forEach(key => {
        if (marcaLower.includes(key) || key.includes(marcaLower)) {
          // Agregar el principio activo base
          principalTerms.push(key);
          // Agregar sinónimos
          PHARMA_KNOWLEDGE.principiosActivos[key].forEach(sinonimo => {
            principalTerms.push(sinonimo);
          });
        }
      });
    }

    // Agregar principio activo explícito si existe
    if (principioActivo && principioActivo.toLowerCase() !== 'n/a') {
      const paLower = principioActivo.toLowerCase();
      principalTerms.push(paLower);

      // Agregar sinónimos del principio activo
      Object.keys(PHARMA_KNOWLEDGE.principiosActivos).forEach(key => {
        if (paLower.includes(key) || key.includes(paLower)) {
          principalTerms.push(key);
          PHARMA_KNOWLEDGE.principiosActivos[key].forEach(sinonimo => {
            principalTerms.push(sinonimo);
          });
        }
      });
    } else if (masterProduct.product) {
      // Si no hay principio activo, intentar con el campo product
      const productName = masterProduct.product.toLowerCase().replace(/\s*\(.*?\)\s*/g, '').trim();
      if (productName) {
        principalTerms.push(productName);

        // Buscar si el nombre del producto coincide con algún principio activo conocido
        Object.keys(PHARMA_KNOWLEDGE.principiosActivos).forEach(key => {
          if (productName.includes(key) || key.includes(productName)) {
            principalTerms.push(key);
            PHARMA_KNOWLEDGE.principiosActivos[key].forEach(sinonimo => {
              principalTerms.push(sinonimo);
            });
          }
        });
      }
    }

    // Eliminar duplicados de términos principales
    const uniquePrincipalTerms = [...new Set(principalTerms)];

    // Construir AND:(OR:term1, OR:term2, ...)
    if (uniquePrincipalTerms.length > 0) {
      const orTerms = uniquePrincipalTerms.map(t => `OR:${t}`).join(', ');
      queryParts.push(`AND:(${orTerms})`);
    }

    // 2. GRUPO AND con ORs internos para forma farmacéutica: AND:(OR:capsula, OR:caps, OR:cap)
    if (formaFarmaceutica && formaFarmaceutica.toLowerCase() !== 'n/a') {
      const formaTerms = [];
      const ffLower = formaFarmaceutica.toLowerCase();
      formaTerms.push(ffLower);

      // Buscar sinónimos de forma farmacéutica
      Object.keys(PHARMA_KNOWLEDGE.formasFarmaceuticas).forEach(key => {
        if (ffLower.includes(key) || key.includes(ffLower)) {
          formaTerms.push(key);
          PHARMA_KNOWLEDGE.formasFarmaceuticas[key].forEach(sinonimo => {
            formaTerms.push(sinonimo);
          });
        }
      });

      // Eliminar duplicados
      const uniqueFormaTerms = [...new Set(formaTerms)];
      if (uniqueFormaTerms.length > 0) {
        const orFormas = uniqueFormaTerms.map(t => `OR:${t}`).join(', ');
        queryParts.push(`AND:(${orFormas})`);
      }
    }

    // 3. AND simple para concentración (solo el número): AND:150
    if (concentracion && concentracion.toLowerCase() !== 'n/a') {
      // Extraer solo el número de la concentración
      const match = concentracion.match(/(\d+)/);
      if (match) {
        const numero = match[1];
        queryParts.push(`AND:${numero}`);
      }
    }

    // 4. Términos NOT para excluir formas incompatibles
    const tipoForma = detectarTipoForma(formaFarmaceutica, presentacion, presentacion);
    if (tipoForma && PHARMA_KNOWLEDGE.formasExcluir[tipoForma]) {
      PHARMA_KNOWLEDGE.formasExcluir[tipoForma].forEach(forma => {
        queryParts.push(`NOT:${forma}`);
      });
    }

    console.log('🔧 Query parts:', queryParts);

    const finalQuery = queryParts.join(', ');
    console.log('🔧 Query final:', finalQuery);

    return finalQuery;
  };

  // Función auxiliar para detectar tipo de forma farmacéutica
  const detectarTipoForma = (forma, nombre, presentacion) => {
    const texto = `${forma} ${nombre} ${presentacion}`.toLowerCase();

    if (texto.includes('capsula') || texto.includes('tableta') || texto.includes('comprimido') ||
        texto.includes('jarabe') || texto.includes('suspension')) {
      return 'oral';
    }
    if (texto.includes('crema') || texto.includes('pomada') || texto.includes('unguento') || texto.includes('gel')) {
      return 'topica';
    }
    if (texto.includes('inyectable') || texto.includes('ampolla') || texto.includes('vial')) {
      return 'inyectable';
    }
    return null;
  };

  // Handler para aplicar query generada al campo de búsqueda
  const handleApplySmartQuery = (masterProduct) => {
    console.log('🔍 Generando query para:', masterProduct);
    const query = generateSmartQuery(masterProduct);
    console.log('📝 Query generada:', query);

    if (query) {
      // Limpiar resultados actuales
      setIntakeProducts([]);
      setIntakeTotal(0);
      setAiSuggestedMatches(new Set()); // Limpiar sugerencias AI previas

      // Cargar la nueva query en el campo de búsqueda
      setIntakeFilterText(query);

      // Resetear paginación primero
      setIntakePagination({ offset: 0, limit: 50 });

      // Luego activar la búsqueda (esto dispara el useEffect)
      setIntakeActiveFilter(query);

      console.log('✅ Query aplicada al campo de búsqueda');
    } else {
      console.log('❌ No se pudo generar query');
    }
  };

  // Función para analizar matches con AI
  const analyzeMatchesWithAI = async () => {
    console.log('🔍 analyzeMatchesWithAI llamado');
    console.log('Estado:', { selectedMasterProduct, intakeTotal, mainActiveSearch, intakeActiveFilter });

    if (!selectedMasterProduct) {
      alert('Debes seleccionar un producto maestro');
      return;
    }

    if (intakeTotal === 0 || (!mainActiveSearch && !intakeActiveFilter)) {
      alert('Debes realizar una búsqueda del lado derecho para tener resultados que analizar');
      return;
    }

    // Verificar que no haya más de 300 productos
    if (intakeTotal > 300) {
      alert(`Hay ${intakeTotal} productos en los resultados de búsqueda.\n\nPor favor, refina tu búsqueda para tener 300 productos o menos antes de usar MATCH AI.`);
      return;
    }

    console.log('✅ Todas las validaciones pasaron, iniciando análisis...');

    setIsAnalyzingAI(true);
    setAiAnalysisProgress({ current: 0, total: intakeTotal });
    setAiSuggestedMatches(new Set()); // Limpiar sugerencias previas

    try {
      // Cargar TODOS los productos del resultado de búsqueda (no solo la página actual)
      const searches = [mainActiveSearch, intakeActiveFilter].filter(Boolean);
      const combinedSearch = searches.join(',');
      const searchParam = combinedSearch ? `&search=${encodeURIComponent(combinedSearch)}` : '';
      const endpoint = `${CLOUD_FUNCTIONS_BASE}/getcatalogproducts?limit=${intakeTotal}&offset=0${searchParam}`;

      const response = await fetch(endpoint);
      const result = await response.json();

      if (!result.success || !result.data) {
        alert('Error cargando productos para análisis');
        setIsAnalyzingAI(false);
        return;
      }

      const allIntakeProducts = result.data.map(p => ({
        id: p.id_catalogo,
        title: p.title,
        marca: p.toma_marca || p.marca,
        sitio: p.sitio,
        precio: p.sale_price || p.discounted_price || p.original_price,
        imagen: p.imagen,
        categoria: p.categoria,
        formaFarmaceutica: p.forma_farmaceutica,
        principioActivo: p.principio_activo,
        presentacion: p.presentacion,
        concentracion: p.concentracion,
        laboratorio: p.laboratorio,
        stock: p.stock,
        sku: p.sku
      }));

      // Preparar datos del producto maestro
      const masterProductData = {
        codigo: selectedMasterProduct.codigo,
        nombre: selectedMasterProduct.presentacion || selectedMasterProduct.nombre,
        marca: selectedMasterProduct.marca,
        principioActivo: selectedMasterProduct.principioActivo,
        concentracion: selectedMasterProduct.concentracion,
        formaFarmaceutica: selectedMasterProduct.formaFarmaceutica,
        presentacion: selectedMasterProduct.presentacion
      };

      // Preparar datos de TODOS los productos de intake
      const intakeProductsData = allIntakeProducts.map((p, idx) => ({
        index: idx,
        id: p.id,
        title: p.title,
        marca: p.marca,
        principioActivo: p.principioActivo,
        concentracion: p.concentracion,
        formaFarmaceutica: p.formaFarmaceutica,
        presentacion: p.presentacion
      }));

      // Crear el prompt para GPT-5 mini
      const prompt = `Eres un experto farmacéutico analizando equivalencias de productos farmacéuticos.

Tu tarea: Identificar cuáles CANDIDATOS son EQUIVALENTES TERAPÉUTICOS del PRODUCTO MAESTRO.

═══════════════════════════════════════════════════════════════════
PRODUCTO MAESTRO (REFERENCIA):
═══════════════════════════════════════════════════════════════════
Código: ${masterProductData.codigo}
Nombre Completo: ${masterProductData.nombre}
Marca: ${masterProductData.marca || 'N/A'}
Principio Activo: ${masterProductData.principioActivo || 'N/A'}
Concentración: ${masterProductData.concentracion || 'N/A'}
Forma Farmacéutica: ${masterProductData.formaFarmaceutica || 'N/A'}
Presentación: ${masterProductData.presentacion || 'N/A'}

═══════════════════════════════════════════════════════════════════
CANDIDATOS A EVALUAR (${intakeProductsData.length} en total):
═══════════════════════════════════════════════════════════════════
${intakeProductsData.map((p, i) => `
[${i}] ID: ${p.id}
Título: ${p.title}
Marca: ${p.marca || 'N/A'}
Principio Activo: ${p.principioActivo || 'N/A'}
Concentración: ${p.concentracion || 'N/A'}
Forma Farmacéutica: ${p.formaFarmaceutica || 'N/A'}
Presentación: ${p.presentacion || 'N/A'}
`).join('\n')}

═══════════════════════════════════════════════════════════════════
CRITERIOS DE EQUIVALENCIA (en orden de importancia):
═══════════════════════════════════════════════════════════════════

🔴 CRITERIOS OBLIGATORIOS (DEBEN cumplirse TODOS):

1. PRINCIPIO ACTIVO EQUIVALENTE

   ⚠️ IMPORTANTE: Reconoce equivalencias entre NOMBRE COMERCIAL y PRINCIPIO ACTIVO

   NOMBRES COMERCIALES CONOCIDOS:
   • BATEN = Fluconazol (antifúngico)
   • DOLEX = Paracetamol / Acetaminofén
   • ADVIL = Ibuprofeno
   • AMOXIL = Amoxicilina
   • VOLTAREN = Diclofenaco

   REGLAS DE EQUIVALENCIA:
   ✓ Si MAESTRO tiene nombre comercial (ej: BATEN) → busca principio activo en CANDIDATO
   ✓ Si MAESTRO tiene "N/A" en principio activo → extrae del nombre/título
   ✓ Paracetamol = Paracetamol = Acetaminofén = Acetaminofen
   ✓ Ibuprofeno = Ibuprofeno
   ✓ BATEN = Fluconazol = Antifúngico

   EJEMPLOS DE MATCH VÁLIDO:
   ✓ MAESTRO "BATEN CAPS 150MG" (P.A.: N/A) ↔ CANDIDATO "Fluconazol 150mg Genfar" (P.A.: Fluconazol)
   ✓ MAESTRO "DOLEX 500MG" (P.A.: N/A) ↔ CANDIDATO "Paracetamol 500mg MK" (P.A.: Paracetamol)

   ✗ RECHAZAR SI:
   ✗ Paracetamol ≠ Ibuprofeno (diferentes principios activos)
   ✗ Amoxicilina ≠ Ampicilina (diferentes antibióticos)

2. CONCENTRACIÓN EXACTA (por unidad de dosificación)
   ✓ 500mg = 500mg = 500 mg
   ✓ 250mg/5ml = 250 mg/5 ml (misma concentración)
   ✓ Para combinaciones: el ORDEN NO IMPORTA
      → Acetaminofén 325mg + Codeína 30mg = Codeína 30mg + Acetaminofén 325mg ✓
   ✗ 500mg ≠ 250mg
   ✗ 250mg/5ml ≠ 500mg/5ml (diferente concentración)
   ✗ 750mg ≠ 250mg

3. FORMA FARMACÉUTICA COMPATIBLE
   ✓ Tableta = Comprimido = Tabletas = Comprimidos
   ✓ Cápsula = Cápsulas = Capsulas
   ✓ Jarabe = Suspensión oral = Solución oral = Polvo para suspensión oral
   ✓ Crema = Ungüento = Pomada (si uso tópico)
   ✗ Tableta ≠ Cápsula
   ✗ Tableta ≠ Inyectable
   ✗ Suspensión oral ≠ Tableta

4. CANTIDAD/VOLUMEN DE PRESENTACIÓN
   ⚠️ REGLA ESTRICTA: Cantidades o volúmenes DIFERENTES = RECHAZAR

   ✓ Maestro "x30 tabletas" + Candidato "x30 tabletas" = MATCH
   ✓ Maestro "60ml" + Candidato "60 ml" = MATCH
   ✓ Maestro "x30" + Candidato "N/A" o vacío = MATCH (sin dato, asumir correcto)
   ✗ Maestro "x30 tabletas" + Candidato "x60 tabletas" = NO MATCH
   ✗ Maestro "60ml" + Candidato "90ml" = NO MATCH
   ✗ Maestro "60ml" + Candidato "45ml" = NO MATCH
   ✗ Maestro "250mg" + Candidato "750mg" = NO MATCH

   EJEMPLOS IMPORTANTES:
   ✗ AMOXICILINA 250MG 60ML ≠ AMOXICILINA 250MG 90ML (diferente volumen)
   ✗ AMOXICILINA 250MG 60ML ≠ AMOXICILINA 250MG 45ML (diferente volumen)
   ✗ PARACETAMOL 500MG X30 ≠ PARACETAMOL 500MG X60 (diferente cantidad)

🟢 CRITERIO FLEXIBLE:

5. MARCA/LABORATORIO
   ✓ NO IMPORTA - puede ser genérico vs original
   ✓ Memphis = Coaspharma = Sanfer = cualquier marca

═══════════════════════════════════════════════════════════════════
INSTRUCCIONES FINALES:
═══════════════════════════════════════════════════════════════════
1. Para cada candidato, verifica los 4 criterios OBLIGATORIOS
2. RECHAZA si hay diferencia en concentración, volumen o cantidad
3. APRUEBA solo si TODO coincide (excepto marca)
4. Si falta dato de cantidad/volumen en candidato → APROBAR
5. Si la cantidad/volumen es DIFERENTE → RECHAZAR SIEMPRE

⚠️ SÉ ESTRICTO: NO apruebes productos con volúmenes o cantidades diferentes.

═══════════════════════════════════════════════════════════════════
RESPUESTA:
═══════════════════════════════════════════════════════════════════
Devuelve SOLO un array JSON con los índices de los productos válidos.

Formato (SOLO JSON, sin markdown ni texto adicional):
[0, 3, 5]

Si ninguno cumple → []`;

      // Llamar a OpenAI vía Cloud Function
      const aiResponse = await fetch(`${CLOUD_FUNCTIONS_BASE}/analyzematches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterProduct: masterProductData,
          intakeProducts: intakeProductsData,
          prompt: prompt
        })
      });

      const aiResult = await aiResponse.json();

      if (aiResult.success && aiResult.matches) {
        // Convertir índices a productos completos
        const matchedProducts = aiResult.matches
          .filter(idx => idx >= 0 && idx < allIntakeProducts.length)
          .map(idx => allIntakeProducts[idx]);

        if (matchedProducts.length > 0) {
          // Crear los matches automáticamente
          try {
            const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/createmanualmatch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                masterCode: selectedMasterProduct.codigo,
                catalogProducts: matchedProducts.map(p => ({
                  id_catalogo: p.id,
                  title: p.title,
                  marca: p.marca,
                  precio: p.precio
                }))
              })
            });

            const matchResult = await response.json();

            if (matchResult.success) {
              // Recargar los matches en el panel
              await loadMatchedCatalogProducts(selectedMasterProduct);

              alert(`✅ AI analizó ${intakeTotal} productos y creó ${matchedProducts.length} match(es) automáticamente.\n\nLos productos matcheados ahora aparecen en el panel central.`);
            } else {
              throw new Error(matchResult.error || 'Error creando matches');
            }
          } catch (matchError) {
            console.error('Error creando matches AI:', matchError);
            alert(`AI encontró ${matchedProducts.length} matches, pero hubo un error al guardarlos: ${matchError.message}`);
          }
        } else {
          alert(`AI analizó ${intakeTotal} productos y no encontró ninguno que coincida con los criterios farmacéuticos estrictos.`);
        }
      } else {
        alert('Error en análisis AI: ' + (aiResult.error || 'Error desconocido'));
      }

    } catch (error) {
      console.error('Error en análisis AI:', error);
      alert('Error analizando matches con AI: ' + error.message);
    } finally {
      setIsAnalyzingAI(false);
      setAiAnalysisProgress({ current: 0, total: 0 });
    }
  };

  // Asignar match manual
  const assignManualMatch = async () => {
    if (!selectedMasterProduct || selectedIntakeProducts.size === 0) {
      alert('Debes seleccionar un producto maestro y al menos un producto de toma');
      return;
    }

    const masterName = selectedMasterProduct.nombre || selectedMasterProduct.presentacion || selectedMasterProduct.product || selectedMasterProduct.codigo;
    if (!window.confirm(`¿Asignar ${selectedIntakeProducts.size} producto(s) de toma al producto maestro "${masterName}"?`)) {
      return;
    }

    setIsSavingMatch(true);
    try {
      const intakeProductsList = Array.from(selectedIntakeProducts).map(id =>
        intakeProducts.find(p => p.id === id)
      );

      console.log('📤 Enviando matcheo manual:', {
        masterCode: selectedMasterProduct.codigo,
        productCount: intakeProductsList.length,
        products: intakeProductsList.map(p => p.id)
      });

      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/createmanualmatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterCode: selectedMasterProduct.codigo,
          catalogProducts: intakeProductsList.map(p => ({
            id_catalogo: p.id,
            title: p.title,
            marca: p.marca,
            sitio: p.sitio,
            precio_venta: p.precio,
            categoria: p.categoria,
            forma_farmaceutica: p.formaFarmaceutica,
            principio_activo: p.principioActivo,
            presentacion: p.presentacion,
            concentracion: p.concentracion,
            laboratorio: p.laboratorio,
            stock: p.stock,
            sku: p.sku
          }))
        })
      });

      const result = await response.json();
      console.log('📥 Respuesta del servidor:', result);

      if (result.success) {
        const message = result.created > 0
          ? `✅ Match manual creado exitosamente:\n${result.created} producto(s) nuevo(s)\n${result.skipped} ya existía(n)`
          : `⚠️ No se crearon nuevos matches:\n${result.skipped} producto(s) ya tenían match con este maestro`;
        alert(message);

        // Limpiar selección y sugerencias de AI primero
        setSelectedIntakeProducts(new Set());
        setSelectedMasterProduct(null);
        setAiSuggestedMatches(new Set());

        // Recargar AMBOS paneles para actualizar los badges de match_count automáticamente
        await Promise.all([
          loadMasterProducts(),
          loadIntakeProducts()
        ]);
      } else {
        alert('Error creando match: ' + result.error);
      }
    } catch (error) {
      console.error('Error asignando match:', error);
      alert('Error asignando match: ' + error.message);
    } finally {
      setIsSavingMatch(false);
    }
  };

  // Función para remover un match individual
  const removeMatch = async (masterCode, catalogId) => {
    if (!window.confirm('¿Estás seguro de que deseas remover este match?')) {
      return;
    }

    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/removematch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterCode,
          catalogId
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Match removido exitosamente');

        // Recargar los matches del panel
        if (matchesPanelData && matchesPanelData.masterProduct) {
          await loadMatchedCatalogProducts(matchesPanelData.masterProduct);
        }

        // Actualizar el conteo en el panel maestro
        await loadMasterProducts();
      } else {
        alert('Error removiendo match: ' + result.error);
      }
    } catch (error) {
      console.error('Error removiendo match:', error);
      alert('Error removiendo match: ' + error.message);
    }
  };

  // Funciones de paginación
  const handleMasterPrevPage = () => {
    setMasterPagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit)
    }));
  };

  const handleMasterNextPage = () => {
    setMasterPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  const handleIntakePrevPage = () => {
    setIntakePagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit)
    }));
  };

  const handleIntakeNextPage = () => {
    setIntakePagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  // Función para copiar ID al portapapeles
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(text);
      setShowCopyToast(true);
      setTimeout(() => {
        setShowCopyToast(false);
      }, 2000);
    }).catch(err => {
      console.error('Error al copiar:', err);
    });
  };

  // Función para mostrar toast de match
  const showMatchNotification = (message) => {
    setMatchToastMessage(message);
    setShowMatchToast(true);
    setTimeout(() => {
      setShowMatchToast(false);
    }, 3000);
  };

  // Funciones para drag and drop
  const handleDragStart = (e, product) => {
    setDraggedProduct(product);

    const dragCount = selectedIntakeProducts.has(product.id)
      ? selectedIntakeProducts.size
      : 1;

    // Crear imagen de drag personalizada para múltiples productos
    if (dragCount > 1) {
      const dragElement = e.target.closest('.product-card');
      if (dragElement) {
        const width = dragElement.offsetWidth;
        const height = dragElement.offsetHeight;

        // Contenedor principal
        const container = document.createElement('div');
        container.style.cssText = `
          position: absolute;
          top: -10000px;
          left: -10000px;
          width: ${width + 20}px;
          height: ${height + 20}px;
        `;

        // Crear las tarjetas del stack (de atrás hacia adelante)
        const numLayers = dragCount === 2 ? 2 : 3;

        for (let i = numLayers - 1; i >= 0; i--) {
          const layer = document.createElement('div');
          const offset = i * 5;

          if (i === 0) {
            // Tarjeta frontal - clonar la real
            const clone = dragElement.cloneNode(true);
            clone.style.cssText = `
              position: absolute;
              top: ${offset}px;
              left: ${offset}px;
              width: ${width}px;
              margin: 0;
              border: 2px solid #10b981;
              box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
            `;

            // Agregar badge
            const badge = document.createElement('div');
            badge.style.cssText = `
              position: absolute;
              top: 10px;
              right: 10px;
              background: #10b981;
              color: white;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 13px;
              font-weight: 600;
              z-index: 1000;
              box-shadow: 0 2px 8px rgba(16, 185, 129, 0.6);
            `;
            badge.textContent = `${dragCount} productos`;
            clone.appendChild(badge);

            container.appendChild(clone);
          } else {
            // Tarjetas de fondo - simples
            layer.style.cssText = `
              position: absolute;
              top: ${offset}px;
              left: ${offset}px;
              width: ${width}px;
              height: ${height}px;
              background: white;
              border: 2px solid ${i === 1 ? '#10b981' : '#34d399'};
              border-radius: 8px;
            `;
            container.appendChild(layer);
          }
        }

        document.body.appendChild(container);
        e.dataTransfer.setDragImage(container, width / 2, 20);

        setTimeout(() => {
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        }, 100);
      }
    }

    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setDraggedProduct(null);
    setIsDraggingOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!isDraggingOver) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    // Solo cambiar el estado si realmente salimos del panel
    // Verificar que no estamos entrando a un elemento hijo
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDraggingOver(false);

    if (!draggedProduct || !selectedMasterProduct) {
      return;
    }

    // Determinar qué productos arrastrar
    const productsToMatch = selectedIntakeProducts.has(draggedProduct.id)
      ? sortedIntakeProducts.filter(p => selectedIntakeProducts.has(p.id))
      : [draggedProduct];

    // Filtrar productos que ya tienen match
    const existingMatchIds = new Set((matchesPanelData?.matches || []).map(m => m.id));
    const newProducts = productsToMatch.filter(p => !existingMatchIds.has(p.id));
    const skippedCount = productsToMatch.length - newProducts.length;

    if (newProducts.length === 0) {
      showMatchNotification(skippedCount === 1 ? 'Este producto ya era match' : 'Todos los productos ya eran match');
      setDraggedProduct(null);
      return;
    }

    // Crear los matches
    try {
      const payload = {
        masterCode: selectedMasterProduct.codigo_presentacion || selectedMasterProduct.codigo,
        catalogProducts: newProducts.map(p => ({
          id_catalogo: p.id,
          title: p.title,
          marca: p.marca,
          sitio: p.sitio,
          precio: p.precio || p.sale_price || p.discounted_price || p.original_price
        }))
      };

      console.log(`🚀 Creating ${newProducts.length} match(es) with payload:`, payload);

      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/createmanualmatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('✅ Server response:', result);

      if (result.success) {
        const created = result.created || 0;
        const total = newProducts.length;

        if (created > 0) {
          const message = created === 1
            ? 'Nuevo Match asignado!'
            : `${created} Matches asignados!${skippedCount > 0 ? ` (${skippedCount} ya existían)` : ''}`;
          showMatchNotification(message);

          // Recargar panel de matches
          await loadMatchedCatalogProducts(selectedMasterProduct);

          // Actualizar badge del producto maestro
          setMasterProducts(prev => prev.map(p =>
            p.codigo === selectedMasterProduct.codigo || p.codigo_presentacion === selectedMasterProduct.codigo_presentacion
              ? { ...p, match_count: (p.match_count || 0) + created }
              : p
          ));

          setSelectedMasterProduct(prev => ({
            ...prev,
            match_count: (prev.match_count || 0) + created
          }));

          // Actualizar badges de productos de catálogo
          const matchedIds = new Set(newProducts.map(p => p.id));
          setIntakeProducts(prev => prev.map(p =>
            matchedIds.has(p.id)
              ? { ...p, match_count: (p.match_count || 0) + 1 }
              : p
          ));

          // Actualizar matchResults para que los badges aparezcan
          const masterCode = selectedMasterProduct.codigo_presentacion || selectedMasterProduct.codigo;
          setMatchResults(prev => {
            const currentMatches = prev[masterCode] || [];
            const newMatches = newProducts.map(p => ({
              catalogId: p.id,
              title: p.title,
              marca: p.marca,
              sitio: p.sitio,
              confidence: 100 // Manual match
            }));
            return {
              ...prev,
              [masterCode]: [...currentMatches, ...newMatches]
            };
          });

          // Limpiar selección después de crear matches
          setSelectedIntakeProducts(new Set());
        } else {
          showMatchNotification('Los productos ya eran match');
        }
      } else {
        showMatchNotification('Error al crear match: ' + (result.error || 'Unknown'));
        console.error('❌ Error response:', result);
      }
    } catch (error) {
      console.error('Error creating match:', error);
      showMatchNotification('Error al crear match');
    }

    setDraggedProduct(null);
  };

  // Función para cerrar panel de matches con animación
  const closeMatchesPanel = () => {
    const panel = document.querySelector('.split-panel-matches');
    if (panel) {
      panel.style.animation = 'shrinkOutToLeft 0.4s cubic-bezier(0.4, 0, 1, 1) forwards';
    }

    setTimeout(() => {
      setShowMatchesPanel(false);
      setMatchesPanelData(null);
      setMatchesPanelLoading(false);
      // Deseleccionar el producto maestro cuando se cierra el panel
      setSelectedMasterProduct(null);
    }, 400);
  };

  // Función para scroll a tarjeta específica por precio
  const scrollToProductByPrice = (targetPrice) => {
    const productId = matchesPanelData.matches.find(p =>
      parseFloat(p.sale_price) === targetPrice
    )?.id;

    if (productId) {
      const element = document.getElementById(`match-card-${productId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Remover la clase primero (por si ya estaba)
        element.classList.remove('price-highlight');

        // Forzar reflow para reiniciar la animación
        void element.offsetWidth;

        // Agregar la clase para iniciar la animación
        element.classList.add('price-highlight');

        // Remover después de que termine la animación
        setTimeout(() => {
          element.classList.remove('price-highlight');
        }, 2000);
      }
    }
  };

  // Función para abrir modal de imagen
  const openImageModal = (imageSrc, title) => {
    setImageModalSrc(imageSrc);
    setImageModalTitle(title);
    setImageModalOpen(true);

    // Resetear estado de análisis previo
    setAiImageAnalysis(null);
    setIsAnalyzingImage(false);
    setScanningProgress(0);

    // Iniciar análisis AI automáticamente
    analyzeImageWithAI(imageSrc);
  };

  // Función para cerrar modal de imagen
  const closeImageModal = () => {
    setImageModalOpen(false);
    setImageModalSrc('');
    setImageModalTitle('');
    setAiImageAnalysis(null);
    setIsAnalyzingImage(false);
    setScanningProgress(0);
  };

  // Función para analizar imagen con AI
  const analyzeImageWithAI = async (imageUrl) => {
    setIsAnalyzingImage(true);
    setScanningProgress(0);

    // Animación de progreso de escaneo
    const progressInterval = setInterval(() => {
      setScanningProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/analyzeproductimage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageUrl })
      });

      if (!response.ok) {
        throw new Error('Error al analizar la imagen');
      }

      const data = await response.json();

      clearInterval(progressInterval);
      setScanningProgress(100);

      if (data.success && data.analysis) {
        setAiImageAnalysis(data.analysis);
      } else {
        setAiImageAnalysis({ error: 'No se pudo analizar la imagen' });
      }
    } catch (error) {
      console.error('Error analizando imagen:', error);
      clearInterval(progressInterval);
      setAiImageAnalysis({ error: error.message });
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  return (
    <div className="split-view-container">
      {/* Header con búsqueda principal centrada */}
      <div className="split-view-header" ref={headerRef}>
        {/* Búsqueda Principal */}
        <div className={`main-search-wrapper ${showMatchesPanel ? 'with-matches-panel' : ''}`}>
          <div className="search-connection-line search-line-left"></div>
          <div className="main-search-container">
            <input
              type="text"
              className="main-search-input"
              placeholder="Búsqueda (Ej: ibuprofeno, AND:250, OR:ibupirac, NOT:jarabe)"
              value={mainSearchText}
              onChange={(e) => setMainSearchText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleMainSearch()}
              title="Operadores disponibles:
• Sin prefijo o AND: = debe contener (ej: ibuprofeno)
• OR: = puede contener como alternativa (ej: OR:ibupirac)
• NOT: = debe excluir (ej: NOT:jarabe)
Separa términos con comas"
            />
            {mainSearchText && (
              <button className="main-search-clear" onClick={handleMainClearSearch}>×</button>
            )}
            <button className="main-search-btn" onClick={handleMainSearch}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          </div>
          <div className="search-connection-line search-line-right"></div>
        </div>
      </div>

      {/* Contenedor de paneles */}
      <div className="split-panels" ref={panelsContainerRef}>
        {/* Panel Izquierdo - Maestro */}
        <div className="split-panel split-panel-master">
          <div className="panel-header">
            <div className="panel-header-top">
              <h3>MAESTRO</h3>
              <div className="panel-header-actions">
                <select
                  className="match-filter-dropdown"
                  value={masterMatchFilter}
                  onChange={(e) => {
                    setMasterMatchFilter(e.target.value);
                    setMasterPagination({ ...masterPagination, offset: 0 });
                  }}
                >
                  <option value="all">Todos los productos</option>
                  <option value="with_match">Productos con Match</option>
                  <option value="without_match">Productos sin Match</option>
                </select>
                {(masterProducts.length > 0 || masterActiveFilter || mainActiveSearch) && (
                  <button
                    className="reset-panel-btn"
                    onClick={handleResetMasterPanel}
                    title="Limpiar resultados"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10"></polyline>
                      <polyline points="23 20 23 14 17 14"></polyline>
                      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            {isViewingMasterMatches && selectedIntakeProduct && (
              <div className="filter-indicator">
                Mostrando matches de: <strong>{selectedIntakeProduct.title || selectedIntakeProduct.name || 'Producto de catálogo'}</strong>
                <button
                  className="clear-filter-btn"
                  onClick={handleResetMasterPanel}
                  title="Ver todos los productos"
                >
                  ✕
                </button>
              </div>
            )}
            {!isViewingMasterMatches && (
              <div className="panel-search">
              {mainActiveSearch && (
                <span className="main-search-indicator">"{mainActiveSearch}"</span>
              )}
              <input
                type="text"
                placeholder={mainActiveSearch ? "Refinar búsqueda..." : "Buscar en maestro..."}
                value={masterFilterText}
                onChange={(e) => setMasterFilterText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleMasterSearch()}
              />
              {masterFilterText && (
                <button className="clear-btn" onClick={handleMasterClearFilter}>×</button>
              )}
              <button className="search-btn" onClick={handleMasterSearch}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
              </div>
            )}
          </div>

          <div className="panel-content">
            {masterLoading ? (
              <div className="loading-state">Cargando...</div>
            ) : masterProducts.length === 0 ? (
              <div className="empty-state">
                <p>No se encontraron resultados</p>
              </div>
            ) : (
              <>
                <div className="products-list">
                  {masterProducts.map(product => {
                    const matchCount = matchResults[product.codigo]?.length || 0;
                    const savedMatchCount = parseInt(product.match_count) || 0;
                    // Verificar selección por codigo o codigo_presentacion
                    const isSelected = selectedMasterProduct && (
                      (selectedMasterProduct.codigo && product.codigo && selectedMasterProduct.codigo === product.codigo) ||
                      (selectedMasterProduct.codigo_presentacion && product.codigo && selectedMasterProduct.codigo_presentacion === product.codigo) ||
                      (selectedMasterProduct.codigo && product.codigo_presentacion && selectedMasterProduct.codigo === product.codigo_presentacion) ||
                      (selectedMasterProduct.codigo_presentacion && product.codigo_presentacion && selectedMasterProduct.codigo_presentacion === product.codigo_presentacion)
                    );
                    const isReferenceProduct = isViewingMasterMatches && isSelected; // Es el producto de referencia si estamos viendo sus matches
                    const isMatchedWithReference = selectedIntakeProduct !== null; // Si hay un producto de referencia de catálogo, todos estos están matcheados con él
                    const searchTerms = getAllSearchTerms(masterActiveFilter);

                    return (
                      <div
                        key={product.codigo}
                        className={`product-card ${isSelected ? 'selected' : ''} ${isMatchedWithReference && !isSelected ? 'matched-with-reference' : ''}`}
                        onClick={() => selectMasterProduct(product)}
                      >
                        <div className="product-card-header">
                          <div className="product-name">{highlightText(product.presentacion || product.nombre || 'N/A', searchTerms)}</div>
                          <div className="product-card-actions">
                            {/* Botón Generar Query AI */}
                            <button
                              className="smart-query-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApplySmartQuery(product);
                              }}
                              title="Generar búsqueda inteligente para este producto"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="product-details">
                          <div><strong>Marca:</strong> <span>{highlightText(product.marca || 'N/A', searchTerms)}</span></div>
                          {product.corporation && <div><strong>Corporation:</strong> <span>{highlightText(product.corporation, searchTerms)}</span></div>}
                          {product.product && <div><strong>Product:</strong> <span>{highlightText(product.product, searchTerms)}</span></div>}
                        </div>
                        <div className="product-code-footer">
                          <div className="footer-left">
                            <span>{product.codigo}</span>
                            <button
                              className="copy-id-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(product.codigo);
                              }}
                              title="Copiar código"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                            </button>
                          </div>
                          {savedMatchCount > 0 && !isViewingMasterMatches && (
                            <div className="footer-badge">
                              <span className="saved-match-badge">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                </svg>
                                {savedMatchCount}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Paginación maestro */}
                <div className="panel-pagination">
                  <button
                    onClick={handleMasterPrevPage}
                    disabled={masterPagination.offset === 0}
                  >
                    Anterior
                  </button>
                  <span>
                    {masterPagination.offset + 1} - {Math.min(masterPagination.offset + masterPagination.limit, masterTotal)} de {masterTotal}
                  </span>
                  <button
                    onClick={handleMasterNextPage}
                    disabled={masterPagination.offset + masterPagination.limit >= masterTotal}
                  >
                    Siguiente
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Botón de Asignar Match entre paneles - Solo mostrar cuando haya selecciones */}
        {/* Panel Intermedio - Matches del Maestro */}
        {showMatchesPanel && matchesPanelData && (
          <div
            className={`split-panel split-panel-matches ${isDraggingOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="panel-header matches-panel-header">
              <div className="matches-header-content">
                <div className="matches-header-left">
                  <div className="master-info-title">
                    {matchesPanelData.masterProduct.nombre || matchesPanelData.masterProduct.presentacion}
                  </div>
                  <div className="master-info-details">
                    <span><strong>Marca:</strong> {matchesPanelData.masterProduct.marca || 'N/A'}</span>
                    <span className="separator">•</span>
                    <span><strong>Código:</strong> {matchesPanelData.masterProduct.codigo}</span>
                  </div>
                </div>
                <div className="matches-header-right">
                  {matchesPanelData.matches.length > 0 && (
                    <span className="saved-match-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                      {matchesPanelData.matches.length}
                    </span>
                  )}
                  <button
                    className="close-matches-panel-btn"
                    onClick={closeMatchesPanel}
                    title="Cerrar panel"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Estadísticas de precios */}
              {matchesPanelData.priceStats && (
                <div className="price-statistics">
                  <div className="price-stat">
                    <span className="price-stat-label">Promedio:</span>
                    <span className="price-stat-value">{formatPrice(matchesPanelData.priceStats.average)}</span>
                  </div>
                  <div
                    className="price-stat clickable"
                    onClick={() => scrollToProductByPrice(matchesPanelData.priceStats.min)}
                    title="Click para ver el producto"
                  >
                    <span className="price-stat-label">Mínimo:</span>
                    <span className="price-stat-value price-min">{formatPrice(matchesPanelData.priceStats.min)}</span>
                  </div>
                  <div
                    className="price-stat clickable"
                    onClick={() => scrollToProductByPrice(matchesPanelData.priceStats.max)}
                    title="Click para ver el producto"
                  >
                    <span className="price-stat-label">Máximo:</span>
                    <span className="price-stat-value price-max">{formatPrice(matchesPanelData.priceStats.max)}</span>
                  </div>
                  <div className="price-stat-count">
                    {matchesPanelData.priceStats.count} producto(s) con precio
                  </div>
                </div>
              )}
            </div>

            {/* Indicador de drag and drop */}
            {isDraggingOver && draggedProduct && (
              <div className="drop-indicator-section">
                <div className="drop-indicator-content">
                  {(() => {
                    const dragCount = selectedIntakeProducts.has(draggedProduct.id) ? selectedIntakeProducts.size : 1;
                    const existingMatchIds = new Set((matchesPanelData?.matches || []).map(m => m.id));
                    const newMatchCount = selectedIntakeProducts.has(draggedProduct.id)
                      ? sortedIntakeProducts.filter(p => selectedIntakeProducts.has(p.id) && !existingMatchIds.has(p.id)).length
                      : existingMatchIds.has(draggedProduct.id) ? 0 : 1;

                    return (
                      <div className="drop-indicator-title">
                        {newMatchCount === 0 ? 'Ya es match' :
                         newMatchCount === 1 ? 'Suelta para agregar como match' :
                         `Suelta para agregar ${newMatchCount} matches`}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Lista de matches */}
            <div className="panel-content">
              {matchesPanelLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Cargando matches...</p>
                </div>
              ) : matchesPanelData.matches.length === 0 ? (
                <div className="empty-state">
                  <p>No hay matches para este producto</p>
                  <p style={{ marginTop: '15px', fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' }}>
                    Arrastra productos desde el panel de Catálogo<br/>hasta aquí para crear matches
                  </p>
                </div>
              ) : (
                <div className="products-list">
                  {matchesPanelData.matches.map((product, index) => (
                    <CatalogProductCard
                      key={product.id}
                      product={product}
                      formatPrice={formatPrice}
                      copyToClipboard={copyToClipboard}
                      openImageModal={openImageModal}
                      className="match-product-card"
                      style={{
                        animationDelay: `${index * 0.05}s`,
                        opacity: 0,
                        animation: 'fadeInUp 0.4s ease-out forwards'
                      }}
                      cardId={`match-card-${product.id}`}
                      footerBadge={
                        <button
                          className="remove-match-btn-compact footer-badge"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMatch(matchesPanelData.masterProduct.codigo, product.id);
                          }}
                          title="Remover match"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18.84 12.25l1.72-1.71h-.02a5.004 5.004 0 00-.12-7.07 5.006 5.006 0 00-6.95 0l-1.72 1.71"></path>
                            <path d="M5.17 11.75l-1.71 1.71a5.004 5.004 0 00.12 7.07 5.006 5.006 0 006.95 0l1.71-1.71"></path>
                          </svg>
                        </button>
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Panel Derecho - Toma/Intake */}
        <div className="split-panel split-panel-intake">
          <div className="panel-header">
            <div className="panel-header-top">
              <h3>{tomaInfo.descripcion}</h3>
              <div className="panel-header-actions">
                <select
                  className="match-filter-dropdown"
                  value={intakeMatchFilter}
                  onChange={(e) => {
                    setIntakeMatchFilter(e.target.value);
                    setIntakePagination({ ...intakePagination, offset: 0 });
                  }}
                >
                  <option value="all">Todos los productos</option>
                  <option value="with_match">Productos con Match</option>
                  <option value="without_match">Productos sin Match</option>
                </select>
                {/* Botón MATCH AI */}
                <button
                  className={`ai-match-btn-header ${!selectedMasterProduct || intakeProducts.length === 0 || (!mainActiveSearch && !intakeActiveFilter) ? 'disabled' : ''}`}
                  onClick={analyzeMatchesWithAI}
                  disabled={!selectedMasterProduct || intakeProducts.length === 0 || (!mainActiveSearch && !intakeActiveFilter) || isAnalyzingAI}
                  title="Analizar matches con AI (requiere producto maestro seleccionado y búsqueda activa)"
                >
                  {isAnalyzingAI ? (
                    <>
                      <div className="spinner small"></div>
                      <span className="ai-btn-text">Analizando...</span>
                    </>
                  ) : (
                    <span className="ai-btn-text">MATCH AI</span>
                  )}
                </button>
                {(intakeProducts.length > 0 || intakeActiveFilter || mainActiveSearch) && (
                  <button
                    className="reset-panel-btn"
                    onClick={handleResetIntakePanel}
                    title="Limpiar resultados"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10"></polyline>
                      <polyline points="23 20 23 14 17 14"></polyline>
                      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="panel-search">
              {mainActiveSearch && (
                <span className="main-search-indicator">"{mainActiveSearch}"</span>
              )}
              <input
                type="text"
                placeholder={mainActiveSearch ? "Refinar búsqueda..." : "Buscar en catálogo..."}
                value={intakeFilterText}
                onChange={(e) => setIntakeFilterText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleIntakeSearch()}
              />
              {intakeFilterText && (
                <button className="clear-btn" onClick={handleIntakeClearFilter}>×</button>
              )}
              <button className="search-btn" onClick={handleIntakeSearch}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
          </div>

          <div className="panel-content">
            {/* Indicador de progreso AI */}
            {isAnalyzingAI && (
              <div className="ai-analysis-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '100%' }}></div>
                </div>
                <div className="progress-text">
                  Analizando {intakeTotal} productos con AI...
                </div>
              </div>
            )}

            {intakeLoading ? (
              <div className="loading-state">Cargando...</div>
            ) : sortedIntakeProducts.length === 0 ? (
              <div className="empty-state">
                <p>No se encontraron resultados</p>
              </div>
            ) : (
              <>
                <div className="products-list">
                  {sortedIntakeProducts.map((product, index) => {
                    // Debug: Log del primer producto para ver tipo de ID
                    if (index === 0) {
                      console.log('🆔 Primer producto de catálogo:', {
                        id: product.id,
                        tipo: typeof product.id,
                        title: product.title
                      });
                    }

                    const matchedMasters = getIntakeProductMatches(product.id);
                    const parsedMatchCount = parseInt(product.match_count, 10);
                    const fallbackMatchCount = Number.isNaN(parsedMatchCount) ? 0 : parsedMatchCount;
                    const badgeCount = matchedMasters.length > 0 ? matchedMasters.length : fallbackMatchCount;
                    const isSelected = selectedIntakeProducts.has(product.id);
                    const isReferenceProduct = selectedIntakeProduct?.id === product.id; // Producto de referencia para ver matches
                    const searchTerms = getAllSearchTerms(intakeActiveFilter);

                    // Verificar si tiene match activo con el maestro seleccionado
                    const hasActiveMatch = selectedMasterProduct && matchResults[selectedMasterProduct.codigo]?.some(m => m.catalogId === product.id);

                    // isMatchedWithMaster solo cuando está en el panel de matches Y es uno de los productos matcheados
                    const isMatchedWithMaster = showMatchesPanel && matchesPanelData?.matches.some(m => m.id === product.id) && !isReferenceProduct;

                    // Debug log para primer producto
                    if (index === 0 && selectedIntakeProducts.size > 0) {
                      console.log('🎨 Renderizando productos:', {
                        productId: product.id,
                        isSelected,
                        selectedIntakeProductsSize: selectedIntakeProducts.size,
                        selectedIntakeProductsArray: Array.from(selectedIntakeProducts)
                      });
                    }

                    return (
                      <React.Fragment key={product.id}>
                        <CatalogProductCard
                          product={product}
                          formatPrice={formatPrice}
                          copyToClipboard={copyToClipboard}
                          openImageModal={openImageModal}
                          searchTerms={searchTerms}
                          highlightText={highlightText}
                          className={`${isSelected ? 'selected' : ''} ${isReferenceProduct ? 'reference-product' : ''} ${isMatchedWithMaster ? 'matched-with-master' : ''} ${hasActiveMatch ? 'active-match' : ''}`}
                          onClick={() => toggleIntakeSelection(product.id)}
                          draggable={showMatchesPanel}
                          onDragStart={(e) => handleDragStart(e, product)}
                          onDragEnd={handleDragEnd}
                          headerBadges={null}
                          footerBadge={
                            badgeCount > 0 && (
                              <div className="footer-badge">
                                <span
                                  className="saved-match-badge"
                                  title="Ver productos maestro asociados"
                                  onClick={(e) => handleCatalogMatchBadgeClick(e, product, matchedMasters)}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                  </svg>
                                  {badgeCount}
                                </span>
                              </div>
                            )
                          }
                        />
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Paginación intake */}
                <div className="panel-pagination">
                  <button
                    onClick={handleIntakePrevPage}
                    disabled={intakePagination.offset === 0}
                  >
                    Anterior
                  </button>
                  <span>
                    {intakePagination.offset + 1} - {Math.min(intakePagination.offset + intakePagination.limit, intakeTotal)} de {intakeTotal}
                  </span>
                  <button
                    onClick={handleIntakeNextPage}
                    disabled={intakePagination.offset + intakePagination.limit >= intakeTotal}
                  >
                    Siguiente
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal para mostrar matches */}
      {showMatchModal && matchModalData && (
        <div className="modal-overlay" onClick={() => setShowMatchModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {matchModalData.type === 'master'
                  ? `Matches del producto ${matchModalData.codigo}`
                  : `Producto asignado a:`
                }
              </h3>
              <button className="modal-close" onClick={() => setShowMatchModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {matchModalData.type === 'master' ? (
                <div className="matches-list">
                  {matchModalData.matches.map((match, idx) => (
                    <div key={idx} className="match-item">
                      <div><strong>{match.title}</strong></div>
                      <div>Marca: {match.marca}</div>
                      <div>Sitio: {match.sitio}</div>
                      <div>Confidence: {match.confidence}%</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="masters-list">
                  {matchModalData.matches.length === 0 ? (
                    <div className="master-item">
                      <div>No se encontraron productos maestro asociados.</div>
                    </div>
                  ) : (
                    matchModalData.matches.map((entry, idx) => {
                      const codigo = typeof entry === 'string' ? entry : entry?.codigo;
                      const nombreDirecto = typeof entry === 'object' && entry !== null ? entry?.nombre : null;
                      const masterProduct = masterProducts.find(p => p.codigo === codigo);
                      const nombreMostrado = nombreDirecto || masterProduct?.nombre || null;

                      return (
                        <div key={idx} className="master-item">
                          <div><strong>{codigo}</strong></div>
                          {nombreMostrado && <div>{nombreMostrado}</div>}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de imagen ampliada con AI Scanner */}
      {imageModalOpen && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="image-modal-body">
              {/* Imagen con animación de escaneo */}
              <div className="image-modal-left">
                <div className="image-scanner-container">
                  <img src={imageModalSrc} alt={imageModalTitle} className="image-modal-img" />

                  {/* Animación de escaneo */}
                  {isAnalyzingImage && (
                    <div className="scanning-overlay">
                      <div className="scanning-line" style={{ top: `${scanningProgress}%` }}></div>
                      <div className="scanning-progress">
                        <svg className="scanning-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                          <line x1="12" y1="22.08" x2="12" y2="12"></line>
                        </svg>
                        <div className="scanning-text">Analizando con AI... {scanningProgress}%</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="image-modal-title">{imageModalTitle}</div>
              </div>

              {/* Panel de análisis AI */}
              <div className="image-modal-right">
                <div className="ai-analysis-panel">
                  <div className="ai-analysis-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                    <h3>Análisis AI del Producto</h3>
                  </div>

                  {isAnalyzingImage && (
                    <div className="ai-analysis-loading">
                      <div className="loading-spinner"></div>
                      <p>Analizando imagen del producto...</p>
                    </div>
                  )}

                  {!isAnalyzingImage && aiImageAnalysis && !aiImageAnalysis.error && (
                    <div className="ai-analysis-results">
                      {aiImageAnalysis.nombre && aiImageAnalysis.nombre !== 'No visible' && (
                        <div className="ai-field">
                          <label>Nombre del Producto:</label>
                          <p>{aiImageAnalysis.nombre}</p>
                        </div>
                      )}
                      {aiImageAnalysis.marca && aiImageAnalysis.marca !== 'No visible' && (
                        <div className="ai-field">
                          <label>Marca / Laboratorio:</label>
                          <p>{aiImageAnalysis.marca}</p>
                        </div>
                      )}
                      {aiImageAnalysis.principio_activo && aiImageAnalysis.principio_activo !== 'No visible' && (
                        <div className="ai-field">
                          <label>Principio Activo:</label>
                          <p>{aiImageAnalysis.principio_activo}</p>
                        </div>
                      )}
                      {aiImageAnalysis.concentracion && aiImageAnalysis.concentracion !== 'No visible' && (
                        <div className="ai-field">
                          <label>Concentración:</label>
                          <p>{aiImageAnalysis.concentracion}</p>
                        </div>
                      )}
                      {aiImageAnalysis.forma_farmaceutica && aiImageAnalysis.forma_farmaceutica !== 'No visible' && (
                        <div className="ai-field">
                          <label>Forma Farmacéutica:</label>
                          <p>{aiImageAnalysis.forma_farmaceutica}</p>
                        </div>
                      )}
                      {aiImageAnalysis.cantidad && aiImageAnalysis.cantidad !== 'No visible' && (
                        <div className="ai-field">
                          <label>Cantidad:</label>
                          <p>{aiImageAnalysis.cantidad}</p>
                        </div>
                      )}
                      {aiImageAnalysis.caracteristicas_visuales && aiImageAnalysis.caracteristicas_visuales !== 'No visible' && (
                        <div className="ai-field">
                          <label>Características del Empaque:</label>
                          <p>{aiImageAnalysis.caracteristicas_visuales}</p>
                        </div>
                      )}
                      {aiImageAnalysis.advertencias && aiImageAnalysis.advertencias !== 'No visible' && (
                        <div className="ai-field">
                          <label>Advertencias:</label>
                          <p>{aiImageAnalysis.advertencias}</p>
                        </div>
                      )}
                      {aiImageAnalysis.resumen && (
                        <div className="ai-field ai-field-summary">
                          <label>Resumen:</label>
                          <p>{aiImageAnalysis.resumen}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {!isAnalyzingImage && aiImageAnalysis && aiImageAnalysis.error && (
                    <div className="ai-analysis-error">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <p>No se pudo analizar la imagen. {aiImageAnalysis.error}</p>
                    </div>
                  )}

                  {!isAnalyzingImage && !aiImageAnalysis && (
                    <div className="ai-analysis-empty">
                      <p>Esperando análisis...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast de copiado */}
      {showCopyToast && (
        <div className="copy-toast">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>ID copiado: <strong>{copiedId}</strong></span>
        </div>
      )}

      {/* Toast de match */}
      {showMatchToast && (
        <div className="match-toast">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>{matchToastMessage}</span>
        </div>
      )}
    </div>
  );
};

export default SplitViewMatcheo;
